import { createContext, useContext, useEffect, useReducer } from 'react';

import { STATUS, STATUS_IDLE } from '@zenbusiness/application-commons-ecommerce/useSubmitOrder';
import { captureException } from '@zenbusiness/application-commons-logging';

import { NAME } from '#client/build-env';
import AutoLogin from '#client/components/AutoLogin';
import ShoppingErrorBoundary from '#client/components/ShoppingErrorBoundary';
import useFlowConfig from '#client/hooks/flow/useFlowConfig';
import useFlowState from '#client/hooks/flow/useFlowState';
import useFlowNavigation from '#client/hooks/useFlowNavigation';

import Interstitial from '../components/Interstitial';
import { CHECKOUT } from '../constants';

const { STEP, STEP_STATUS, STEP_ORDER, ACTION } = CHECKOUT;

const CheckoutStateContext = createContext(null);
CheckoutStateContext.displayName = 'CheckoutStateContext';
const CheckoutDispatchContext = createContext(() => null);
CheckoutDispatchContext.displayName = 'CheckoutDispatchContext';

const initialState = Object.freeze({
  password: '',
  cardToken: null,
  paymentMethodId: null,
  paymentMethodType: null,
  activeStep: CHECKOUT.STEP.CART,
  stepStatus: {
    [STEP.ACCOUNT]: STEP_STATUS.INCOMPLETE,
    [STEP.BUSINESS]: STEP_STATUS.INCOMPLETE,
    [STEP.CART]: STEP_STATUS.ACTIVE,
    [STEP.PAYMENT]: STEP_STATUS.INCOMPLETE
  },
  placeOrder: {
    status: STATUS_IDLE,
    error: null
  }
});

// compute derived state of the steps statuses every time the active step changes
const computeStepStatus = (_activeStep, stepOrder = STEP_ORDER) =>
  stepOrder.reduce((acc, step) => {
    if (step === _activeStep) {
      acc[step] = STEP_STATUS.ACTIVE;
    } else if (stepOrder.indexOf(step) < stepOrder.indexOf(_activeStep)) {
      // if the step is before the active step, it should be marked as complete
      acc[step] = STEP_STATUS.COMPLETE;
    } else {
      // if the step is after the active step, it should be marked as incomplete
      acc[step] = STEP_STATUS.INCOMPLETE;
    }

    return acc;
  }, initialState.stepStatus);

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION.SET_PASSWORD:
      return {
        ...state,
        password: action.payload
      };
    case ACTION.SET_PAYMENT_INSTRUMENT:
      return {
        ...state,
        cardToken: action.payload.cardToken,
        paymentMethodId: action.payload.paymentMethodId,
        // if not set explicitly, move it to null
        paymentMethodType: action.payload.paymentMethodType || null,
        placeOrder: {
          status: STATUS_IDLE,
          error: null
        }
      };
    case ACTION.NEXT_STEP: {
      const nextStep = action.stepOrder.find((step) => {
        const stepIdx = action.stepOrder.indexOf(step);
        const activeStepIdx = action.stepOrder.indexOf(state.activeStep);
        return stepIdx > activeStepIdx;
      });

      if (!nextStep) {
        captureException(new Error('No next checkout step found'));
        return state;
      }

      return {
        ...state,
        activeStep: nextStep,
        stepStatus: computeStepStatus(nextStep, action.stepOrder)
      };
    }
    case ACTION.GO_TO_STEP: {
      if (!action.stepOrder.includes(action.payload)) {
        captureException(new Error('Invalid checkout step'));
        return state;
      }

      return {
        ...state,
        activeStep: action.payload,
        stepStatus: computeStepStatus(action.payload, action.stepOrder)
      };
    }
    case ACTION.PLACE_ORDER_START: {
      return {
        ...state,
        placeOrder: {
          status: STATUS.PENDING,
          error: null
        }
      };
    }
    case ACTION.PLACE_ORDER_SUCCESS: {
      return {
        ...state,
        placeOrder: {
          status: STATUS.SUCCESS,
          error: null
        }
      };
    }
    case ACTION.PLACE_ORDER_ERROR: {
      return {
        ...state,
        placeOrder: {
          status: STATUS.FAILED,
          error: action.payload
        }
      };
    }
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

function CheckoutProvider({ children }) {
  const { postOrderRedirect, postOrderAutoLogin } = useFlowConfig();
  const { contactInfo, set, clear } = useFlowState();
  const { nextPage, goToNextPage } = useFlowNavigation();
  const [state, dispatch] = useReducer(reducer, initialState);

  const nextFlowPage = nextPage?.path;
  const hasOrderSucceeded = state.placeOrder.status === STATUS.SUCCESS;
  const shouldAutoLogin = postOrderAutoLogin || !nextFlowPage;

  // Go to cart step, as current implementation ignores initialState on remount
  useEffect(() => {
    dispatch({ type: ACTION.GO_TO_STEP, payload: STEP.CART, stepOrder: STEP_ORDER });
  }, []);

  // Handle navigating to the next page when available after order is placed.
  useEffect(() => {
    if (hasOrderSucceeded && !shouldAutoLogin) {
      goToNextPage();
      return;
    }

    // Update the flow progress if auto-login is enabled...
    if (hasOrderSucceeded && postOrderAutoLogin) {
      if (nextFlowPage) {
        // If there is a next page, set the progress so we can come back to it after auto-login.
        set('lastPage', nextFlowPage);
      } else {
        // If there is no next page, clear the flow state so we can start over
        clear();
      }
    }
  }, [
    clear,
    goToNextPage,
    hasOrderSucceeded,
    shouldAutoLogin,
    nextFlowPage,
    postOrderAutoLogin,
    set
  ]);

  // Handle auto login and redirection to post order redirect from flow config.
  if (hasOrderSucceeded && shouldAutoLogin) {
    return (
      <Interstitial>
        <ShoppingErrorBoundary
          fallback={<AutoLogin.Error email={contactInfo?.email} />}
          id={`${NAME}/App/CheckoutProvider`}
        >
          <AutoLogin
            email={contactInfo?.email}
            password={state?.password}
            redirectTo={postOrderRedirect}
          />
        </ShoppingErrorBoundary>
      </Interstitial>
    );
  }

  return (
    <CheckoutStateContext.Provider value={state}>
      <CheckoutDispatchContext.Provider value={dispatch}>
        {children}
      </CheckoutDispatchContext.Provider>
    </CheckoutStateContext.Provider>
  );
}

function useCheckoutStateContext() {
  const context = useContext(CheckoutStateContext);
  if (!context) {
    throw new Error('useCheckoutStateContext hook must be used within a CheckoutProvider');
  }
  return context;
}

function useCheckoutDispatchContext() {
  const context = useContext(CheckoutDispatchContext);
  if (!context) {
    throw new Error('useCheckoutDispatchContext hook must be used within a CheckoutProvider');
  }
  return context;
}

function useCheckoutContext() {
  return [useCheckoutStateContext(), useCheckoutDispatchContext()];
}

export { CheckoutProvider, useCheckoutContext };
